import { ExternalLink } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaCheckboxField } from "@/shared/components/forms/ColocaCheckboxField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { legalLinksFor } from "@/shared/constants/legal";
import useTranslations from "@/shared/lang/useTranslations";

import type { VerificationConsentRequest, VerificationPurpose } from "../types/verification-api.types";

const CONSENT_FORM_ID = "verification-consent-form";

/**
 * Both boxes are required. `accepted_terms` is optional on the wire (`accepted_terms?: boolean` — the backend
 * takes either), so this is our call: a terms box the reader may leave blank under a button that says "I
 * accept and continue" is an incoherence that a real link to the document makes impossible to ignore.
 *
 * Takes its message instead of living at module scope, because both refines DO reach the screen. This file
 * used to argue they could not — the submit button is disabled until both boxes are ticked, and a disabled
 * submit blocks implicit submission too — but `useZodForm` validates on `onTouched`, so ticking a box and
 * unticking it touches the field and renders its message with nothing submitted at all. Until this was fixed,
 * that message was Zod's own "Invalid input": English, on a Spanish screen.
 *
 * The sentence has to be passed in. `shared/lang/zodErrorMap.ts` localizes the bounds Zod raises by itself,
 * but deliberately lets `custom` — the code a `.refine()` raises — fall through to Zod's default, so that it
 * can never reword a rule that worded itself. A refine that wants a sentence has to carry one.
 */
const buildConsentSchema = (requiredMessage: string) =>
  z
    .object({
      accepted_data_processing: z.boolean(),
      accepted_terms: z.boolean(),
    })
    .refine((value) => value.accepted_data_processing, {
      path: ["accepted_data_processing"],
      message: requiredMessage,
    })
    .refine((value) => value.accepted_terms, {
      path: ["accepted_terms"],
      message: requiredMessage,
    });

export interface ConsentGateProps {
  purpose: VerificationPurpose;
  submitting?: boolean;
  onAccept: (values: VerificationConsentRequest) => void;
}

/** Consent screen: the data-processing authorization and the terms, both required. */
export function ConsentGate({ purpose, submitting, onAccept }: ConsentGateProps) {
  const { t, activeLanguage } = useTranslations("verification");

  const consentSchema = useMemo(() => buildConsentSchema(t("consent_required")), [t]);
  const form = useZodForm(consentSchema, { accepted_data_processing: false, accepted_terms: false });
  const acceptedProcessing = form.watch("accepted_data_processing");
  const acceptedTerms = form.watch("accepted_terms");

  return (
    <div className="space-y-6">
      <h1 className="text-center text-2xl font-semibold text-foreground">{t("consent_title")}</h1>

      <ColocaForm form={form} id={CONSENT_FORM_ID} onSubmit={(values) => onAccept(values)}>
        <div className="space-y-4">
          <ColocaCheckboxField
            name="accepted_data_processing"
            label={t(purpose === "KYB" ? "consent_kyb_required" : "consent_kyc_required")}
          />
          {/*
            The document is a line of its own under the box it belongs to, NOT a link inside the label.
            A `<label>`'s accessible name is built from its descendants, so an anchor in there becomes part
            of the CHECKBOX's own name, and screen readers read those words without saying they are a link
            — the reader is asked to accept a document they are never told is reachable. Out here the box
            announces exactly its sentence and the link announces as a link.

            `ml-6` is the checkbox's `size-4` plus the field's `gap-2`, so the link starts on the same
            vertical as the label text above it and reads as belonging to that box rather than to the form.
            `inline-block` — not `inline-flex` — because vertical padding has to apply (an inline box
            ignores it, so the touch target would not actually grow) while the icon must stay in normal
            inline flow: as a flex item it would be the first thing squeezed onto a line of its own, which
            is exactly the failure this screen just came from.

            Underlined always, never on hover alone: a link told apart from its surroundings by colour only
            fails WCAG 1.4.1. And `text-foreground` rather than the brand green, which beside a legal
            sentence reads as a button.
          */}
          <div className="space-y-1">
            <ColocaCheckboxField name="accepted_terms" label={t("consent_terms_label")} />
            <a
              href={legalLinksFor(activeLanguage).terms}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-6 inline-block rounded py-2 text-sm font-medium text-foreground underline decoration-muted-foreground/50 underline-offset-4 transition-colors hover:decoration-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              {t("consent_terms_link")}
              <ExternalLink aria-hidden="true" className="ml-1 inline size-3.5 align-[-0.125em]" />
              <span className="sr-only">{t("consent_terms_new_tab")}</span>
            </a>
          </div>
        </div>
      </ColocaForm>

      <ColocaButton
        type="submit"
        form={CONSENT_FORM_ID}
        variant="primary"
        className="w-full"
        disabled={!acceptedProcessing || !acceptedTerms}
        loading={submitting}
      >
        {t("consent_cta")}
      </ColocaButton>
    </div>
  );
}
